import { Document, Page, Text, View } from '@react-pdf/renderer';
import moment from 'moment';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import Watermark from 'src/components/custom_components/CustomWatermark';

import { Disease } from 'src/models/Disease.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { ListOfPositiveHeatmap } from 'src/models/Grading.model';
import { handleNSI18Next } from 'src/models/Language.model';
import {
  ReportTemplate,
  ReportTemplateName,
  ReportTemplateNameEnum,
} from 'src/models/ReportTemplate.model';
import { ReportType } from 'src/models/ReportType.model';
import { Session } from 'src/models/Session.model';

import { cls } from 'src/utils/reactPdfStyles';

import AppointmentSection from './AppointmentSection';
import ClassificationTableSection from './ClassificationTableSection';
import CvdSection from './CvdSection';
import FooterSection from './FooterSection';
import HeaderSection from './HeaderSection';
import HeatmapSection from './HeatmapSection';
import ImageSection from './ImageSection';
import InfoSection from './InfoSection';
import NotesSection from './NotesSection';
import ResultSection from './ResultSection';
import SegmentationSection from './SegmentationSection/SegmentationSection';
import SummarySection from './SummarySection';
import VersionSection from './VersionSection';

interface IAssurePlusReportProps {
  session: Session;
  historyIndex?: number;
  type: ReportType;
  logo: string;
}

const AssurePlusReport = ({
  session,
  historyIndex,
  type,
  logo,
}: IAssurePlusReportProps) => {
  const { handleCommonT, handleReportT } = handleNSI18Next();

  const {
    practiceSettings: { watermark, shouldShowHeatmap },
    currentUser,
  } = useRemoteBoundStore.getState();

  const isAuscvd = !!session.template?.includes('auscvd');

  const formattedTimestamp = historyIndex
    ? moment(session?.history?.[historyIndex]?.SavedTime?.toString()).format(
        'YYYY-MM-DD HH:mm:ss',
      )
    : '';
  return (
    <Document>
      <Page size="A4" style={cls('body')}>
        <View style={cls('p-4')}>
          <HeaderSection
            type={type}
            title={handleReportT(`title.${type}`)}
            logo={logo}
          ></HeaderSection>

          <InfoSection session={session}></InfoSection>

          <ImageSection type={type} session={session}></ImageSection>

          <ResultSection
            type={type}
            session={session}
            historyIndex={historyIndex}
            showNotes={true}
            currentUser={currentUser ?? ({} as GalenDataUser)}
          ></ResultSection>

          <Text style={cls('p mb-0')}>
            <Text style={cls('small b')}>
              {handleReportT(`footerSection.title`)}
            </Text>
          </Text>

          <FooterSection></FooterSection>
        </View>
        {watermark ? <Watermark text={watermark} /> : <View></View>}
      </Page>

      {type === ReportType.CLINICAL &&
        shouldShowHeatmap &&
        ListOfPositiveHeatmap(session, historyIndex).map((disease) => (
          <Page size="A4" style={cls('body')} key={disease}>
            <View style={cls('p-4')}>
              <HeaderSection
                type={type}
                title={handleReportT(`title.${type}`)}
                logo={logo}
              ></HeaderSection>

              <HeatmapSection
                session={session}
                disease={disease}
                historyIndex={historyIndex}
              ></HeatmapSection>

              {historyIndex && (
                <VersionSection
                  currentUser={currentUser ?? ({} as GalenDataUser)}
                  formattedTimestamp={formattedTimestamp}
                />
              )}

              <Text style={cls('p pt-4 mb-0')}>
                <Text style={cls('small b')}>
                  {handleReportT(`footerSection.title`)}
                </Text>
              </Text>

              <FooterSection></FooterSection>
            </View>
            {watermark ? <Watermark text={watermark} /> : <View></View>}
          </Page>
        ))}

      {type === ReportType.PATIENT && (
        <Page size="A4" style={cls('body')}>
          <View style={cls('p-4')}>
            <HeaderSection type={type} title="" logo={logo}></HeaderSection>

            <SummarySection session={session} historyIndex={historyIndex} />

            <NotesSection session={session} />
            <AppointmentSection session={session} />

            <Text style={cls('p section-text lead mt-5 pt-5')}>
              {handleCommonT('contact.visitWebsite')}
            </Text>

            <Text style={cls('p pt-4 mb-0')}>
              <Text style={cls('small b')}>
                {handleReportT(`footerSection.title`)}
              </Text>
            </Text>

            <FooterSection />
          </View>
          {watermark ? <Watermark text={watermark} /> : <View></View>}
        </Page>
      )}

      {ReportTemplateName(
        session.template ?? ReportTemplate.ASSUREPLUS_LARGE_EYE_CVD,
      ) === ReportTemplateNameEnum.EYE_DISEASES_CVD && (
        <Page size="A4" style={cls('body')}>
          <View style={cls('px-4 pt-4')}>
            <HeaderSection
              type={type}
              title={handleReportT(`title.${type}`)}
              logo={logo}
            ></HeaderSection>

            <InfoSection session={session}></InfoSection>

            <SegmentationSection
              session={session}
              type={type}
            ></SegmentationSection>

            <CvdSection
              session={session}
              disease={Disease.WHOCVD_RISK}
              type={type}
              showDiseaseName={isAuscvd}
              showNotes={false}
            ></CvdSection>

            {historyIndex && (
              <VersionSection
                currentUser={currentUser ?? ({} as GalenDataUser)}
                formattedTimestamp={formattedTimestamp}
              />
            )}

            <Text style={cls('p pt-4 mb-0')}>
              <Text style={cls('small b')}>
                {handleReportT(`footerSection.title`)}
              </Text>
            </Text>

            <FooterSection></FooterSection>
          </View>
          {watermark ? <Watermark text={watermark} /> : <View></View>}
        </Page>
      )}

      <Page size="A4" style={cls('body')}>
        <View style={cls('p-4')}>
          <HeaderSection
            type={type}
            title={handleReportT(`title.${type}`)}
            logo={logo}
          ></HeaderSection>

          <ClassificationTableSection session={session} />

          <FooterSection></FooterSection>
        </View>
        {watermark ? <Watermark text={watermark} /> : <View></View>}
      </Page>
    </Document>
  );
};

export default AssurePlusReport;
